/**
 * POSSEN — Modern cart
 *
 * Line quantity / removal happen over the Cart AJAX API, then the cart page is
 * re-fetched and only its data regions are swapped back in. Re-rendering with
 * Liquid keeps money formatting, market pricing, discounts and app hooks
 * correct instead of re-implementing them in JS.
 */
(function () {
  'use strict';

  var UNDO_TIMEOUT = 10000;
  var NOTE_DEBOUNCE = 800;
  var QTY_DEBOUNCE = 600;

  var root = null;
  var pendingUndo = null;
  var undoTimer = null;
  var noteTimer = null;
  var qtyTimers = {};
  var inFlight = 0;
  var stickyObserver = null;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ---------------------------------------------------------------- setup */

  function init() {
    var next = document.querySelector('[data-cart-root]');
    if (!next) return;

    root = next;
    bind(root);
    setStickyOffset();
    initStickyBar();
    renderUndo();

    window.addEventListener('resize', debounce(setStickyOffset, 250));
  }

  function bind(scope) {
    scope.addEventListener('click', onClick);
    scope.addEventListener('input', onInput);
    scope.addEventListener('change', onChange);

    var form = scope.querySelector('.CartModern__form');
    if (form) form.addEventListener('submit', onSubmit);
  }

  /**
   * The summary is sticky beneath the fixed site header.
   */
  function setStickyOffset() {
    if (!root) return;
    var header = document.querySelector('header.row, .shopify-section-header, #header');
    var height = header ? header.getBoundingClientRect().height : 0;
    root.style.setProperty('--pc-sticky-offset', Math.round(height + 24) + 'px');
  }

  /**
   * Reveal the mobile checkout bar once the summary CTA scrolls out of view.
   */
  function initStickyBar() {
    var bar = root.querySelector('[data-sticky-bar]');
    var anchor = root.querySelector('[data-checkout]');

    if (stickyObserver) {
      stickyObserver.disconnect();
      stickyObserver = null;
    }

    if (!bar || !anchor || !('IntersectionObserver' in window)) {
      document.body.classList.remove('has-cart-stickybar');
      return;
    }

    stickyObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        toggleStickyBar(bar, !entry.isIntersecting);
      });
    }, { rootMargin: '0px 0px -20% 0px' });

    stickyObserver.observe(anchor);
  }

  function toggleStickyBar(bar, show) {
    bar.classList.toggle('is-visible', show);
    bar.toggleAttribute('inert', !show);
    bar.setAttribute('aria-hidden', show ? 'false' : 'true');

    var cta = bar.querySelector('button');
    if (cta) cta.setAttribute('tabindex', show ? '0' : '-1');

    document.body.classList.toggle('has-cart-stickybar', show);
  }

  /* --------------------------------------------------------------- events */

  function onClick(event) {
    var step = event.target.closest('[data-dec], [data-inc]');
    if (step) {
      event.preventDefault();
      if (step.getAttribute('aria-disabled') === 'true') return;

      var row = step.closest('.LineItem');
      var input = row && row.querySelector('[data-qty-input]');
      if (!row || !input) return;

      var quantity = parseInt(input.value, 10);
      if (isNaN(quantity)) quantity = 1;
      quantity = step.hasAttribute('data-dec') ? quantity - 1 : quantity + 1;
      if (quantity < 0) quantity = 0;

      input.value = quantity;

      if (quantity === 0) {
        removeLine(row);
      } else {
        updateLine(row, quantity, step.hasAttribute('data-dec') ? 'data-dec' : 'data-inc');
      }
      return;
    }

    var remove = event.target.closest('[data-remove]');
    if (remove) {
      event.preventDefault();
      var removeRow = remove.closest('.LineItem');
      if (removeRow) removeLine(removeRow);
      return;
    }

    var undo = event.target.closest('[data-undo]');
    if (undo) {
      event.preventDefault();
      restoreLine();
      return;
    }

    var retry = event.target.closest('[data-retry]');
    if (retry) {
      event.preventDefault();
      var retryRow = retry.closest('.LineItem');
      if (!retryRow) return;

      var wanted = parseInt(retryRow.dataset.retryQuantity, 10);
      if (isNaN(wanted)) return;

      if (wanted === 0) {
        removeLine(retryRow);
      } else {
        updateLine(retryRow, wanted, 'data-qty-input');
      }
    }
  }

  function onInput(event) {
    var note = event.target.closest('[data-note-field]');
    if (note) {
      queueNote(note);
      return;
    }

    var input = event.target.closest('[data-qty-input]');
    if (!input) return;

    var row = input.closest('.LineItem');
    if (!row) return;

    var key = row.dataset.key;
    clearTimeout(qtyTimers[key]);
    qtyTimers[key] = setTimeout(function () {
      commitQuantity(row, input);
    }, QTY_DEBOUNCE);
  }

  function onChange(event) {
    var input = event.target.closest('[data-qty-input]');
    if (!input) return;

    var row = input.closest('.LineItem');
    if (!row) return;

    clearTimeout(qtyTimers[row.dataset.key]);
    commitQuantity(row, input);
  }

  function commitQuantity(row, input) {
    var quantity = parseInt(input.value, 10);
    var current = parseInt(row.dataset.lineQuantity, 10) || 1;

    if (isNaN(quantity) || quantity < 0) {
      input.value = current;
      return;
    }

    if (quantity === current) return;

    if (quantity === 0) {
      removeLine(row);
    } else {
      updateLine(row, quantity, 'data-qty-input');
    }
  }

  function onSubmit(event) {
    if (inFlight > 0) {
      event.preventDefault();
      announce('Your cart is still updating. One moment.');
      return;
    }

    var submitter = event.submitter;
    if (submitter && submitter.name === 'checkout') {
      submitter.setAttribute('aria-disabled', 'true');
    }
  }

  /* ----------------------------------------------------------- operations */

  function updateLine(row, quantity, focusRole) {
    return change(row, quantity, { focusKey: row.dataset.key, focusRole: focusRole });
  }

  function removeLine(row) {
    pendingUndo = readLine(row);
    return change(row, 0, { focusRole: 'undo' }).then(function (ok) {
      if (!ok) {
        pendingUndo = null;
        return;
      }
      armUndoTimer();
    });
  }

  function armUndoTimer() {
    clearTimeout(undoTimer);
    undoTimer = setTimeout(function () {
      pendingUndo = null;
      renderUndo();
    }, UNDO_TIMEOUT);
  }

  function change(row, quantity, options) {
    setRowBusy(row, true);
    inFlight++;

    return request('/cart/change.js', { id: row.dataset.key, quantity: quantity })
      .then(function () {
        return refresh(options);
      })
      .then(function () {
        announce(
          quantity === 0
            ? (row.dataset.lineTitle || 'Item') + ' removed from your cart'
            : 'Quantity updated to ' + quantity
        );
        return true;
      })
      .catch(function (error) {
        setRowBusy(row, false);
        revertQuantity(row, quantity);
        showRowError(row, error);
        announce('We could not update your cart. Please try again.');
        return false;
      })
      .then(function (ok) {
        inFlight = Math.max(0, inFlight - 1);
        return ok;
      });
  }

  function restoreLine() {
    if (!pendingUndo) return Promise.resolve();

    var data = pendingUndo;
    var payload = {
      id: data.variantId,
      quantity: data.quantity,
      properties: data.properties || {}
    };
    if (data.sellingPlan) payload.selling_plan = data.sellingPlan;

    pendingUndo = null;
    clearTimeout(undoTimer);
    inFlight++;

    return request('/cart/add.js', payload)
      .then(function () {
        return refresh({});
      })
      .then(function () {
        announce((data.title || 'Item') + ' restored to your cart');
      })
      .catch(function (error) {
        announce('We could not restore that item. Please try again.');

        // Shopify answered and refused: the cart is untouched, so keep offering
        // undo. Anything else (network drop) is ambiguous — re-read the cart.
        if (error && error.responseReceived) {
          pendingUndo = data;
          renderUndo();
          armUndoTimer();
          return;
        }

        return refresh({}).catch(function () {
          window.location.reload();
        });
      })
      .then(function () {
        inFlight = Math.max(0, inFlight - 1);
      });
  }

  function queueNote(field) {
    var status = root.querySelector('[data-note-status]');
    if (status) status.textContent = 'Saving…';

    clearTimeout(noteTimer);
    noteTimer = setTimeout(function () {
      request('/cart/update.js', { note: field.value })
        .then(function () {
          if (status) status.textContent = 'Saved automatically.';
        })
        .catch(function () {
          if (status) status.textContent = 'We could not save your note. It will be sent at checkout.';
        });
    }, NOTE_DEBOUNCE);
  }

  function request(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          var error = new Error(data && data.description ? data.description : 'Cart request failed');
          error.responseReceived = true;
          throw error;
        }
        return data;
      });
    });
  }

  /* ------------------------------------------------------------- renderer */

  /**
   * Re-fetch /cart and swap the Liquid-rendered regions back in.
   */
  function refresh(options) {
    options = options || {};
    if (root) root.setAttribute('aria-busy', 'true');

    return fetch(root.dataset.cartUrl || '/cart', {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Could not load the cart');
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var next = doc.querySelector('[data-cart-root]');
        if (!next) throw new Error('Cart markup not found');

        var nowEmpty = !next.querySelector('[data-cart-items]');
        var wasEmpty = !root.querySelector('[data-cart-items]');

        if (nowEmpty !== wasEmpty) {
          replaceRoot(next);
        } else {
          swap(next, '[data-cart-items]');
          swap(next, '[data-summary-body]');
          swap(next, '[data-ship-meter]');
          swap(next, '[data-pants-event-meter]');
          swap(next, '[data-cart-count]');
          swap(next, '[data-sticky-total]');
        }

        syncHeaderCount(doc);
        renderUndo();
        root.removeAttribute('aria-busy');
        restoreFocus(options);
        notify(readCartJson(next));
      })
      .catch(function (error) {
        if (root) root.removeAttribute('aria-busy');
        throw error;
      });
  }

  function readCartJson(scope) {
    var node = scope.querySelector('[data-cart-json]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  /**
   * Tell the rest of the page the cart changed. Third-party listeners are not
   * allowed to break this flow, so every handoff is isolated.
   */
  function notify(cart) {
    try {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart, source: 'cart-modern' } }));
    } catch (error) {
      /* a listener threw — not our problem to propagate */
    }

    if (!window.jQuery || !cart) return;

    try {
      window.jQuery(document).trigger('cart.requestComplete', [cart]);
    } catch (error) {
      /* legacy CartJS listener threw — the cart itself is already up to date */
    }
  }

  function swap(next, selector) {
    var target = root.querySelector(selector);
    var source = next.querySelector(selector);
    if (!target) return;

    if (!source) {
      target.innerHTML = '';
      return;
    }

    target.innerHTML = source.innerHTML;
    target.className = source.className;
  }

  function replaceRoot(next) {
    if (stickyObserver) {
      stickyObserver.disconnect();
      stickyObserver = null;
    }
    document.body.classList.remove('has-cart-stickybar');

    root.replaceWith(next);
    root = next;
    bind(root);
    setStickyOffset();
    initStickyBar();
  }

  function syncHeaderCount(doc) {
    ['.cart-count', '.counter'].forEach(function (selector) {
      var source = doc.querySelector(selector);
      if (!source) return;
      document.querySelectorAll(selector).forEach(function (node) {
        if (root.contains(node)) return;
        node.textContent = source.textContent;
      });
    });

    var sourceLink = doc.querySelector('.cart-link');
    var links = document.querySelectorAll('.cart-link');
    if (sourceLink && links.length) {
      links.forEach(function (link) {
        link.classList.toggle('active', sourceLink.classList.contains('active'));
      });
    }
  }

  /**
   * The undo strip lives in JS state so it survives every region swap.
   */
  function renderUndo() {
    var host = root.querySelector('[data-cart-undo]');
    if (!host) return;

    if (!pendingUndo) {
      host.innerHTML = '';
      return;
    }

    host.innerHTML =
      '<div class="LineItem--removed">' +
      '<span>' + escapeHtml(pendingUndo.title || 'Item') + ' removed from your cart.</span>' +
      '<button type="button" class="LineItem__undo" data-undo>' +
      '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      '<path d="M3 8a5 5 0 1 1 1.6 3.7"/><path d="M3 4.5V8h3.5"/></svg>' +
      'Undo</button></div>';
  }

  /* ----------------------------------------------------------- row states */

  function setRowBusy(row, busy) {
    if (!row || !row.isConnected) return;

    if (busy) {
      row.setAttribute('aria-busy', 'true');
    } else {
      row.removeAttribute('aria-busy');
    }

    row.querySelectorAll('button, input').forEach(function (control) {
      if (busy) {
        control.setAttribute('aria-disabled', 'true');
      } else {
        control.removeAttribute('aria-disabled');
      }
    });
  }

  /**
   * The stepper updates optimistically. If the server refuses, put the field
   * back to the quantity the cart actually holds and remember what was wanted
   * so "Try again" repeats the intent rather than the stale value.
   */
  function revertQuantity(row, attempted) {
    if (!row || !row.isConnected) return;

    row.dataset.retryQuantity = attempted;

    var input = row.querySelector('[data-qty-input]');
    if (input && row.dataset.lineQuantity) input.value = row.dataset.lineQuantity;
  }

  function showRowError(row, error) {
    if (!row || !row.isConnected) return;

    var existing = row.querySelector('.LineItem__error');
    if (existing) existing.remove();

    var node = document.createElement('div');
    node.className = 'LineItem__error';
    node.setAttribute('role', 'alert');
    node.innerHTML =
      '<span>' + escapeHtml(error && error.message ? error.message : 'Update failed.') + '</span>' +
      '<button type="button" class="LineItem__retry" data-retry>Try again</button>';
    row.appendChild(node);
  }

  /* --------------------------------------------------------------- helpers */

  function readLine(row) {
    var properties = {};
    try {
      properties = JSON.parse(row.dataset.lineProperties);
    } catch (error) {
      properties = null;
    }

    // Liquid serialises a line with no properties as `[]`; /cart/add.js only
    // accepts an object here.
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
      properties = {};
    }

    return {
      title: row.dataset.lineTitle,
      variantId: row.dataset.lineVariant,
      quantity: parseInt(row.dataset.lineQuantity, 10) || 1,
      sellingPlan: row.dataset.lineSellingPlan || null,
      properties: properties
    };
  }

  /**
   * Put focus back on the control the shopper was using before the swap.
   */
  function restoreFocus(options) {
    if (!options.focusRole) return;

    var target = null;

    if (options.focusRole === 'undo') {
      target = root.querySelector('[data-undo]');
    } else if (options.focusKey) {
      var row = root.querySelector('.LineItem[data-key="' + cssEscape(options.focusKey) + '"]');
      if (row) target = row.querySelector('[' + options.focusRole + ']');
    }

    if (target) target.focus({ preventScroll: true });
  }

  function announce(message) {
    var region = root.querySelector('[data-cart-aria]') || document.querySelector('[data-cart-aria]');
    if (!region) return;
    region.textContent = message;
    setTimeout(function () {
      if (region.textContent === message) region.textContent = '';
    }, 2000);
  }

  function escapeHtml(value) {
    var node = document.createElement('span');
    node.textContent = value == null ? '' : String(value);
    return node.innerHTML;
  }

  function cssEscape(value) {
    if (window.CSS && window.CSS.escape) return window.CSS.escape(value);
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var context = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  window.CartModern = {
    refresh: function () {
      return refresh({});
    },
    announce: announce
  };
})();
